/**
 * PDF generation + print for the order receipt on the web.
 *
 * Two surfaces:
 *   1. exportOrderPDF — uses lazy-loaded jspdf to produce a downloadable
 *      PDF file (uses the same template as the mobile `expo-print` flow).
 *   2. printOrderReceipt — opens a hidden iframe, injects the receipt
 *      HTML, and triggers the browser's native print dialog. Works on
 *      both desktop and mobile browsers.
 *
 * The QR is embedded as a base64 PNG (caller is responsible for
 * generating it via `generateTrackQR`).
 *
 * Snapshot at creation: the function only reads fields from the order
 * argument. No network calls.
 */
import type { Language } from '../i18n';
import { useTranslation } from '../i18n';

type Translator = ReturnType<typeof useTranslation>['t'];

export interface OrderReceiptData {
  release_code: string;
  customer_name: string;
  customer_contact: string | null;
  device: string;
  device_brand: string | null;
  device_model: string | null;
  imei_serial: string | null;
  issue: string | null;
  status_name: string;
  branch_name?: string | null;
  org_name?: string | null;
  created_at: string;
}

function esc(value: string | null | undefined): string {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function fmtDate(iso: string | null, locale: Language): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

/**
 * Build an OrderReceiptData from the edge function's order response.
 * The create-order edge function returns { order, release_code } where
 * `order` is the result of .select().single(). This helper maps the
 * response fields to the receipt type expected by the PDF/print helpers.
 *
 * Accepts a partial object — only the required receipt fields are read.
 * Allows passing the raw order object directly without type ceremony.
 */
export function buildOrderReceiptData(order: Record<string, unknown>): OrderReceiptData {
  return {
    release_code: (order.release_code as string) ?? '',
    customer_name: (order.customer_name as string) ?? '',
    customer_contact: (order.customer_contact as string | null) ?? null,
    device: (order.device as string) ?? '',
    device_brand: (order.device_brand as string | null) ?? null,
    device_model: (order.device_model as string | null) ?? null,
    imei_serial: (order.imei_serial as string | null) ?? null,
    issue: (order.issue as string | null) ?? null,
    status_name: (order.status as string) ?? (order.status_name as string) ?? 'Received',
    branch_name: (order.branch_name as string | null) ?? null,
    org_name: (order.org_name as string | null) ?? null,
    created_at: (order.created_at as string) ?? new Date().toISOString(),
  };
}

/**
 * Build the receipt HTML string for embedding in a print-only iframe.
 * Split layout: business copy (top) + client copy (bottom) with watermark,
 * separated by a cut line. Portrait letter-size. 3-column compact grid.
 */
function buildReceiptHtml(
  order: OrderReceiptData,
  qrBase64: string,
  locale: Language,
  t: Translator,
): string {
  const trackUrl = `${window.location.origin}/${locale}/track?code=${encodeURIComponent(order.release_code)}`;
  const orgName = order.org_name ?? 'TellowUp';
  const branchName = order.branch_name ?? '';
  const generatedAt = new Date().toISOString();

  const qrImg = qrBase64
    ? `<img src="${esc(qrBase64)}" width="48" height="48" alt="QR" />`
    : '';

  const oneCopy = (pillClass: string, pillText: string, watermarkHtml: string) => `
<div class="copy"${watermarkHtml ? ' style="position:relative;overflow:hidden;"' : ''}>
  ${watermarkHtml}
  <div class="copy-header">
    <div class="title-block">
      <h1>${esc(t('orderReceipt.title'))}</h1>
      <div class="org">${esc(orgName)}${branchName ? ' · ' + esc(branchName) : ''}</div>
    </div>
    <div class="pill ${pillClass}">${esc(pillText)}</div>
  </div>
  <div class="grid">
    <div class="col">
      <h2>${esc(t('orderReceipt.order'))}</h2>
      <div class="field"><span class="key">${esc(t('orderReceipt.orderId'))}:</span> ${esc(order.release_code) || '—'}</div>
      <div class="field"><span class="key">${esc(t('orderReceipt.releaseCode'))}:</span> ${esc(order.release_code) || '—'}</div>
      <div class="field"><span class="key">${esc(t('orderReceipt.received'))}:</span> ${esc(fmtDate(order.created_at, locale))}</div>
      <div class="field"><span class="status-pill">${esc(order.status_name || '—')}</span></div>
    </div>
    <div class="col">
      <h2>${esc(t('orderReceipt.customer'))}</h2>
      <div class="field">${esc(order.customer_name) || '—'}</div>
      <div class="field" style="color:#666;">${esc(order.customer_contact) || '—'}</div>
    </div>
    <div class="col">
      <h2>${esc(t('orderReceipt.equipment'))}</h2>
      <div class="field">${esc(order.device) || '—'}</div>
      <div class="field" style="color:#666;">${esc(order.device_brand) || '—'}${order.device_brand && order.device_model ? ' · ' : ''}${esc(order.device_model) || ''}</div>
      <div class="field" style="color:#666;">${order.imei_serial ? 'SN: ' + esc(order.imei_serial) : ''}</div>
      <div class="field" style="color:#b91c1c;font-size:8px;">${order.issue ? esc(t('orderReceipt.issue')) + ': ' + esc(order.issue) : ''}</div>
    </div>
  </div>
  <div class="qr-footer">
    <div class="qr-block">
      ${qrImg}
      <div>
        <div class="qr-caption">${esc(t('orderReceipt.scanToTrack'))}</div>
        <div class="qr-url">${esc(trackUrl)}</div>
      </div>
    </div>
    <div class="footer-info">
      <div>${esc(t('orderReceipt.generatedAt'))}: ${esc(generatedAt)}</div>
      <div>${esc(t('orderReceipt.poweredBy'))}</div>
    </div>
  </div>
</div>`;

  return `<!doctype html>
<html lang="${locale}">
<head>
<meta charset="utf-8" />
<title>${esc(t('orderReceipt.title'))}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, system-ui, "Helvetica Neue", Arial, sans-serif; color: #111; margin: 0; padding: 0; font-size: 10px; }
  .copy { padding: 12px 16px 10px 16px; }
  .copy-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
  .title-block { flex: 1; }
  .copy-header h1 { margin: 0; font-size: 14px; color: #059669; line-height: 1.2; }
  .org { margin-top: 0; font-size: 9px; color: #888; }
  .pill { display: inline-block; padding: 1px 8px; border-radius: 3px; font-size: 8px; font-weight: 700; text-transform: uppercase; flex-shrink: 0; margin-left: 8px; }
  .pill-business { background: #e0f2fe; color: #075985; }
  .pill-client { background: #fef3c7; color: #92400e; }
  .grid { display: flex; gap: 8px; margin-bottom: 8px; }
  .col { flex: 1; background: #f9fafb; border-radius: 4px; padding: 6px 8px; }
  .col h2 { font-size: 8px; text-transform: uppercase; color: #999; letter-spacing: 0.5px; margin: 0 0 3px 0; font-weight: 600; }
  .field { font-size: 9px; color: #111; line-height: 1.5; }
  .field .key { color: #999; }
  .status-pill { display: inline-block; padding: 0 6px; background: #e0f2fe; color: #075985; border-radius: 8px; font-weight: 600; font-size: 8px; }
  .cut-line { text-align: center; padding: 3px 0; background: #fafafa; font-size: 7px; color: #bbb; letter-spacing: 2px; border-top: 2px dashed #bbb; border-bottom: 2px dashed #bbb; user-select: none; }
  .qr-footer { display: flex; justify-content: space-between; align-items: center; padding-top: 8px; border-top: 1px solid #eee; }
  .qr-block { display: flex; align-items: center; gap: 8px; }
  .qr-block img { display: block; border-radius: 3px; flex-shrink: 0; }
  .qr-caption { margin: 0; font-size: 8px; font-weight: 600; }
  .qr-url { margin: 0; font-size: 7px; color: #999; }
  .footer-info { text-align: right; font-size: 8px; color: #999; line-height: 1.4; }
  .watermark { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-20deg); font-size: 52px; font-weight: 900; color: rgba(5, 150, 105, 0.06); white-space: nowrap; pointer-events: none; z-index: 0; letter-spacing: 3px; }
  @media print { body { padding: 0; margin: 0; } }
</style>
</head>
<body>
  ${oneCopy('pill-business', t('orderReceipt.businessCopy'), '')}
  <div class="cut-line">${esc(t('orderReceipt.cutHere'))}</div>
  ${oneCopy('pill-client', t('orderReceipt.clientCopy'), `<div class="watermark">${esc(t('orderReceipt.clientCopyWatermark'))}</div>`)}
</body>
</html>`;
}

/**
 * Build the receipt as a downloadable PDF using jspdf + html2canvas.
 * Injects the receipt HTML into a hidden off-screen iframe, captures
 * it with html2canvas, and adds the resulting image to a jsPDF document.
 * This guarantees visual parity with the screen print view.
 */
export async function exportOrderPDF(
  order: OrderReceiptData,
  locale: Language,
  qrBase64: string,
  t: Translator,
): Promise<void> {
  const [{ jsPDF }, html2canvas] = await Promise.all([
    import('jspdf'),
    import('html2canvas').then(m => m.default),
  ]);

  const html = buildReceiptHtml(order, qrBase64, locale, t);

  // Create a hidden off-screen iframe to render the receipt HTML.
  // Wider iframe for landscape PDF capture.
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '-9999px';
  iframe.style.bottom = '0';
  iframe.style.width = '1000px';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  try {
    // Write the HTML directly into the iframe's contentDocument.
    // This is synchronous — no need to wait for a load event.
    const iframeDoc = iframe.contentDocument!;
    iframeDoc.open();
    iframeDoc.write(html);
    iframeDoc.close();

    // Capture the rendered receipt with html2canvas
    const canvas = await html2canvas(
      iframeDoc.body,
      { scale: 2, useCORS: true, logging: false },
    );

    const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.addImage(
      canvas.toDataURL('image/png'),
      'PNG',
      0,
      0,
      pageWidth,
      pageHeight,
    );

    const fileName = `tellowup-${order.release_code || 'order'}.pdf`;
    doc.save(fileName);
  } finally {
    // Clean up the iframe
    try {
      document.body.removeChild(iframe);
    } catch {
      // already removed
    }
  }
}

/**
 * Open the native browser print dialog with a print-only receipt view.
 * Uses an off-screen iframe to avoid disrupting the current page.
 * The print stylesheet (in the injected HTML) ensures only the receipt
 * content is printed.
 */
export function printOrderReceipt(
  order: OrderReceiptData,
  locale: Language,
  qrBase64: string,
  t: Translator,
): void {
  const html = buildReceiptHtml(order, qrBase64, locale, t);
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '-9999px';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const cleanup = () => {
    try {
      document.body.removeChild(iframe);
    } catch {
      // already removed
    }
  };

  iframe.addEventListener('load', () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } finally {
      // Give the print dialog a moment to open before removing the iframe
      setTimeout(cleanup, 1000);
    }
  });

  iframe.srcdoc = html;
}
